import { generateMqttClient } from '@pages/_loader/initMqttClient.loader';
import type { MqttClient } from 'mqtt';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

interface DynamicMessageContextState {
  mqttClient: MqttClient | undefined;
  isConnecting: boolean;
  connectionError: Error | null;
}

const initialState: DynamicMessageContextState = {
  mqttClient: undefined,
  isConnecting: true,
  connectionError: null,
};

const DynamicMessageContext =
  createContext<DynamicMessageContextState>(initialState);

interface DynamicMessageContextProviderProps {
  children: ReactNode;
}

export const DynamicMessageContextProvider = ({
  children,
}: DynamicMessageContextProviderProps) => {
  const [mqttClient, setMqttClient] = useState<MqttClient>();
  const [isConnecting, setIsConnecting] = useState(true);
  const [connectionError, setConnectionError] = useState<Error | null>(null);

  const setupMqttClient = useCallback(async () => {
    try {
      const clientInstance = await generateMqttClient();

      setMqttClient(clientInstance);
      setIsConnecting(clientInstance.reconnecting);
      setConnectionError(null);

      clientInstance.on('connect', () => {
        setIsConnecting(false);
        setConnectionError(null);
      });

      clientInstance.on('error', (err) => {
        setConnectionError(new Error(String(err)));
      });

      clientInstance.on('reconnect', () => {
        setIsConnecting(true);
      });

      return clientInstance;
    } catch (error) {
      setMqttClient(undefined);
      setIsConnecting(false);
      setConnectionError(
        error instanceof Error ? error : new Error(String(error)),
      );
      return undefined;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    let clientInstance: MqttClient | null = null;

    setupMqttClient()
      .then((client) => {
        if (isMounted && client) {
          clientInstance = client;
        }
      })
      .catch((error) => {
        if (isMounted) {
          setIsConnecting(false);
          setConnectionError(new Error(`Connection failed: ${error.message}`));
        }
      });

    return () => {
      isMounted = false;
      if (clientInstance) {
        clientInstance?.removeAllListeners();
        clientInstance?.end(true);
      }
    };
  }, [setupMqttClient]);

  const contextValue = useMemo(
    () => ({
      mqttClient,
      isConnecting,
      connectionError,
    }),
    [mqttClient, isConnecting, connectionError],
  );

  return (
    <DynamicMessageContext.Provider value={contextValue}>
      {children}
    </DynamicMessageContext.Provider>
  );
};

export const useDynamicMessageContext = (): DynamicMessageContextState => {
  const context = useContext(DynamicMessageContext);

  if (context === undefined) {
    throw new Error(
      'useDynamicMessageContext must be used within a DynamicMessageContextProvider',
    );
  }

  return context;
};
